import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Papa from 'papaparse';
import AdvancedDataGrid from '../../components/AdvancedDataGrid';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Users, Mail, Upload, Search, Loader2, CheckCircle2, XCircle, AlertTriangle,
  Trash2, Download, X, Shield, Phone, Eye, FileSpreadsheet, RefreshCw, Target,
  Save, List, Send
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Smart Column Mapping ───

const SYSTEM_FIELDS = [
  { key: 'first_name', label: 'First Name', synonyms: ['firstname', 'first', 'fname', 'first_name'] },
  { key: 'last_name', label: 'Last Name', synonyms: ['lastname', 'last', 'lname', 'last_name'] },
  { key: 'work_email', label: 'Work Email', synonyms: ['emailw', 'email_work', 'workemail', 'work_email', 'email'] },
  { key: 'personal_email', label: 'Personal Email', synonyms: ['emailp', 'email_personal', 'personalemail', 'personal_email'] },
  { key: 'emails', label: 'Emails (combined)', synonyms: ['emails', 'email_addresses', 'all_emails'] },
  { key: 'email_json', label: 'Email JSON', synonyms: ['email_json', 'emailjson'] },
  { key: 'linkedin_url', label: 'LinkedIn URL', synonyms: ['linkedinprofileurl', 'linkedin_url', 'profile_url', 'normalizedurl', 'linkedin', 'profileurl'] },
  { key: 'title', label: 'Title', synonyms: ['title', 'jobtitle', 'job_title', 'position'] },
  { key: 'company', label: 'Company', synonyms: ['company', 'currentcompany', 'company_name', 'employer', 'organization'] },
  { key: 'city', label: 'City', synonyms: ['city', 'location'] },
  { key: 'state', label: 'State', synonyms: ['state', 'region', 'province'] },
  { key: 'phone_mobile', label: 'Mobile Phone', synonyms: ['phonem', 'mobile', 'mobile_phone', 'cell'] },
  { key: 'phone_work', label: 'Work Phone', synonyms: ['phonew', 'work_phone', 'phone', 'direct_phone'] },
  { key: 'phone_home', label: 'Home Phone', synonyms: ['phoneh', 'home_phone'] },
  { key: 'phone_json', label: 'Phone JSON', synonyms: ['phone_json', 'phonejson'] },
  { key: 'phones', label: 'Phones (combined)', synonyms: ['phones', 'phone_numbers'] },
];

function autoMap(csvHeaders) {
  const mappings = {};
  for (const field of SYSTEM_FIELDS) {
    const normalized = field.synonyms.map(s => s.toLowerCase());
    const match = csvHeaders.find(h => normalized.includes(h.toLowerCase().replace(/[\s\-_]/g, '')));
    if (match) {
      mappings[field.key] = match;
    } else {
      const fuzzy = csvHeaders.find(h => {
        const hNorm = h.toLowerCase().replace(/[\s\-_]/g, '');
        return normalized.some(s => hNorm.includes(s) || s.includes(hNorm));
      });
      if (fuzzy && !Object.values(mappings).includes(fuzzy)) {
        mappings[field.key] = fuzzy;
      }
    }
  }
  return mappings;
}

// ─── Typeahead Input ───

function TypeaheadInput({ value, onChange, field, placeholder }) {
  const [suggestions, setSuggestions] = useState([]);
  const [focused, setFocused] = useState(false);
  const debounceRef = useRef(null);

  const fetchSuggestions = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/api/email-lists/typeahead?q=${encodeURIComponent(q)}&field=${field}&limit=8`, { withCredentials: true });
        setSuggestions(Array.isArray(data) ? data : []);
      } catch { setSuggestions([]); }
    }, 200);
  };

  return (
    <div className="relative">
      <Input
        value={value}
        onChange={e => { onChange(e.target.value); fetchSuggestions(e.target.value); }}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 200)}
        placeholder={placeholder}
        className="h-9 text-sm"
      />
      {focused && suggestions.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((s, i) => {
            const label = typeof s === 'string' ? s : `${s.first_name || ''} ${s.last_name || ''} — ${s.primary_email || ''}`;
            return (
              <button
                key={i}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-50 last:border-0"
                onMouseDown={() => { onChange(typeof s === 'string' ? s : s.primary_email || `${s.first_name} ${s.last_name}`); setSuggestions([]); }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Stats Card ───

function StatCard({ icon: Icon, label, value, color = 'text-gray-900', bgColor = 'bg-gray-50', sub }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
      <div className="flex items-center gap-2.5">
        <div className={`${bgColor} rounded-lg p-2`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <div>
          <p className="text-lg font-bold text-gray-900">{typeof value === 'number' ? value.toLocaleString() : value}</p>
          <p className="text-[11px] text-gray-500 leading-tight">{label}</p>
          {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

// ─── Import Wizard ───

function ImportWizard({ onClose, onComplete, tags }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [batchName, setBatchName] = useState('');
  const [targetName, setTargetName] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [mappings, setMappings] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const fileRef = useRef(null);

  const parseFile = (f) => {
    setFile(f);
    setBatchName(f.name.replace(/\.(csv|xlsx?)$/i, '') + ' — ' + new Date().toLocaleDateString());
    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setCsvHeaders(results.meta.fields || []);
        setCsvRows(results.data);
        setMappings(autoMap(results.meta.fields || []));
        setStep(2);
      },
      error: () => toast.error('Failed to parse CSV'),
    });
  };

  const rowsWithEmail = useMemo(() => {
    if (!csvRows.length) return 0;
    return csvRows.filter(row => {
      const w = mappings.work_email, p = mappings.personal_email, e = mappings.emails, j = mappings.email_json;
      return (w && row[w]?.trim()) || (p && row[p]?.trim()) || (e && row[e]?.trim()) || (j && row[j]?.trim());
    }).length;
  }, [csvRows, mappings]);

  const startImport = async () => {
    setImporting(true);
    setStep(4);
    const columnMappings = {};
    for (const [k, v] of Object.entries(mappings)) { if (v) columnMappings[k] = v; }

    const CHUNK = 3000;
    const totalChunks = Math.ceil(csvRows.length / CHUNK);
    let result = { rows_imported: 0, rows_skipped_no_email: 0, rows_skipped_duplicate: 0, rows_skipped_suppressed: 0, batch_id: '' };
    setProgress({ current: 0, total: csvRows.length });

    for (let i = 0; i < totalChunks; i++) {
      try {
        const { data } = await axios.post(`${API}/api/email-lists/import`, {
          batch_name: batchName, target_name: targetName, tag_ids: selectedTags,
          column_mappings: columnMappings, rows: csvRows.slice(i * CHUNK, (i + 1) * CHUNK),
        }, { withCredentials: true });
        result.rows_imported += data.rows_imported || 0;
        result.rows_skipped_no_email += data.rows_skipped_no_email || 0;
        result.rows_skipped_duplicate += data.rows_skipped_duplicate || 0;
        result.rows_skipped_suppressed += data.rows_skipped_suppressed || 0;
        if (!result.batch_id) result.batch_id = data.batch_id;
      } catch (e) {
        toast.error(`Chunk ${i + 1} failed: ${e.response?.data?.detail || e.message}`);
      }
      setProgress({ current: Math.min((i + 1) * CHUNK, csvRows.length), total: csvRows.length });
    }
    result.total_rows = csvRows.length;
    setImportResult(result);
    setImporting(false);
    setStep(5);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Import Email List — Step {step} of 5</CardTitle>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {step === 1 && (
            <div className="text-center py-8">
              <FileSpreadsheet className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-sm text-gray-600 mb-4">Upload a CSV file with email contacts</p>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={e => e.target.files[0] && parseFile(e.target.files[0])} />
              <Button onClick={() => fileRef.current?.click()} className="gap-2"><Upload className="w-4 h-4" /> Select CSV File</Button>
            </div>
          )}

          {step === 2 && (
            <>
              <p className="text-sm text-gray-600 mb-3">
                Parsed <strong>{csvRows.length.toLocaleString()}</strong> rows from <strong>{file?.name}</strong>
              </p>
              <div>
                <Label className="text-sm mb-1 block">Batch Name *</Label>
                <Input value={batchName} onChange={e => setBatchName(e.target.value)} placeholder="e.g., LinkedIn SalesNav Q2 2026" />
              </div>
              <div>
                <Label className="text-sm mb-1 block">Target Name</Label>
                <Input value={targetName} onChange={e => setTargetName(e.target.value)} placeholder="e.g., Recruiters, Staffing Companies, Decision Makers" />
                <p className="text-xs text-gray-400 mt-1">Applied to every contact in this batch for filtering</p>
              </div>
              {tags.length > 0 && (
                <div>
                  <Label className="text-sm mb-1 block">Tags (optional)</Label>
                  <div className="flex flex-wrap gap-2">
                    {tags.map(t => (
                      <button key={t.id} onClick={() => setSelectedTags(prev => prev.includes(t.id) ? prev.filter(x => x !== t.id) : [...prev, t.id])}
                        className={`text-xs px-2.5 py-1 rounded-full border ${selectedTags.includes(t.id) ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <Button onClick={() => setStep(3)} disabled={!batchName.trim()} className="w-full">Next: Map Columns</Button>
            </>
          )}

          {step === 3 && (
            <>
              <p className="text-sm text-gray-500 mb-2">Map CSV columns to contact fields.</p>
              <div className="max-h-[400px] overflow-y-auto space-y-2">
                {SYSTEM_FIELDS.map(field => (
                  <div key={field.key} className="flex items-center gap-3">
                    <span className="text-sm text-gray-700 w-36 shrink-0">{field.label}</span>
                    <select value={mappings[field.key] || ''} onChange={e => setMappings(prev => ({ ...prev, [field.key]: e.target.value }))}
                      className="flex-1 text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
                      <option value="">— skip —</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    {mappings[field.key] && <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />}
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <p className="text-xs text-gray-400 mb-1">Preview (first 3 rows)</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-gray-50">
                      {['Name', 'Email', 'Company', 'LinkedIn'].map(h => <th key={h} className="text-left py-1.5 px-2 font-medium text-gray-500">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {csvRows.slice(0, 3).map((row, i) => (
                        <tr key={i} className="border-t">
                          <td className="py-1 px-2">{row[mappings.first_name]} {row[mappings.last_name]}</td>
                          <td className="py-1 px-2 text-blue-600">{row[mappings.work_email] || row[mappings.personal_email] || row[mappings.emails] || '—'}</td>
                          <td className="py-1 px-2">{row[mappings.company] || '—'}</td>
                          <td className="py-1 px-2 truncate max-w-[150px]">{row[mappings.linkedin_url] || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800"><strong>{rowsWithEmail.toLocaleString()}</strong> of {csvRows.length.toLocaleString()} rows have email and will be imported.</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                <Button onClick={startImport} disabled={!mappings.work_email && !mappings.personal_email && !mappings.emails && !mappings.email_json} className="flex-1 gap-2">
                  <Upload className="w-4 h-4" /> Import {rowsWithEmail.toLocaleString()} Contacts
                </Button>
              </div>
            </>
          )}

          {step === 4 && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 animate-spin text-blue-500 mx-auto mb-4" />
              <p className="text-sm text-gray-600 mb-2">Importing contacts...</p>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div className="bg-blue-500 h-3 rounded-full transition-all" style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }} />
              </div>
              <p className="text-xs text-gray-400">{progress.current.toLocaleString()} / {progress.total.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-2">Email verification starts automatically after import</p>
            </div>
          )}

          {step === 5 && importResult && (
            <div className="space-y-4">
              <div className="text-center">
                <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
                <p className="text-lg font-semibold">Import Complete</p>
                <p className="text-xs text-gray-400">Email verification running in background</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold">{importResult.total_rows?.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">Total Rows</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-green-600">{importResult.rows_imported?.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">Imported</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-yellow-600">{importResult.rows_skipped_no_email?.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">No Email</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-blue-600">{importResult.rows_skipped_duplicate?.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">Duplicates</p>
                </div>
              </div>
              <Button onClick={() => { onComplete(); onClose(); }} className="w-full">Done</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Main Page ───

export default function EmailListPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [batches, setBatches] = useState([]);
  const [tags, setTags] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [showSaveList, setShowSaveList] = useState(false);
  const [saveListName, setSaveListName] = useState('');
  const [saveListDesc, setSaveListDesc] = useState('');
  const [savingList, setSavingList] = useState(false);

  // Server-side filters
  const [search, setSearch] = useState('');
  const [batchNameFilter, setBatchNameFilter] = useState('');
  const [targetNameFilter, setTargetNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [verifyFilter, setVerifyFilter] = useState('');
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const LIMIT = 100;

  const searchDebounce = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
  }, [search]);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * LIMIT, limit: LIMIT, sort: sortField, order: sortOrder });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (batchNameFilter) params.set('batch_name', batchNameFilter);
      if (targetNameFilter) params.set('target_name', targetNameFilter);
      if (statusFilter) params.set('status', statusFilter);
      if (verifyFilter) params.set('verify_result', verifyFilter);
      const { data } = await axios.get(`${API}/api/email-lists/contacts?${params}`, { withCredentials: true });
      setContacts(data.contacts);
      setTotal(data.total);
    } catch (e) {
      console.error('Failed to load contacts', e);
    }
    setLoading(false);
  }, [page, debouncedSearch, batchNameFilter, targetNameFilter, statusFilter, verifyFilter, sortField, sortOrder]);

  const fetchMeta = useCallback(async () => {
    // Fast: just the total count — lights up the "Total Contacts" card in <50ms
    // even when the collection has hundreds of thousands of rows.
    axios
      .get(`${API}/api/email-lists/count`, { withCredentials: true })
      .then(({ data }) => setStats((prev) => ({ ...prev, total: data.total })))
      .catch(() => {});

    // Heavier: full aggregate stats (valid/invalid/risky/opens/etc.). Fire in
    // parallel; other cards populate as soon as this resolves.
    axios
      .get(`${API}/api/email-lists/stats`, { withCredentials: true })
      .then(({ data }) => setStats((prev) => ({ ...prev, ...data })))
      .catch(() => {});

    // Lookup lists for the filter dropdowns.
    try {
      const [b, t] = await Promise.all([
        axios.get(`${API}/api/email-lists/batches`, { withCredentials: true }),
        axios.get(`${API}/api/email-lists/tags`, { withCredentials: true }),
      ]);
      setBatches(b.data);
      setTags(t.data);
    } catch {}
  }, []);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchContacts(); }, [fetchContacts]);
  const refreshAll = () => { fetchContacts(); fetchMeta(); };

  const hasFilters = !!(debouncedSearch || batchNameFilter || targetNameFilter || statusFilter || verifyFilter);

  const saveAsList = async () => {
    if (!saveListName.trim()) { toast.error('List name is required'); return; }
    setSavingList(true);
    try {
      const filters = {};
      if (debouncedSearch) filters.search = debouncedSearch;
      if (batchNameFilter) filters.batch_name = batchNameFilter;
      if (targetNameFilter) filters.target_name = targetNameFilter;
      if (statusFilter) filters.status = statusFilter;
      if (verifyFilter) filters.verify_result = verifyFilter;
      await axios.post(`${API}/api/email-lists/saved-lists`, {
        name: saveListName.trim(),
        description: saveListDesc,
        filters,
      }, { withCredentials: true });
      toast.success(`List "${saveListName}" saved with ${total.toLocaleString()} contacts`);
      setShowSaveList(false);
      setSaveListName('');
      setSaveListDesc('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save list');
    }
    setSavingList(false);
  };

  const deleteBatch = async (batchId) => {
    if (!window.confirm('Delete this entire batch and all its contacts?')) return;
    await axios.delete(`${API}/api/email-lists/batches/${batchId}`, { withCredentials: true });
    toast.success('Batch deleted');
    setBatchNameFilter('');
    refreshAll();
  };

  // AdvancedDataGrid columns
  const gridColumns = useMemo(() => [
    { key: 'first_name', label: 'First Name', sortable: true, filterable: true, visible: true },
    { key: 'last_name', label: 'Last Name', sortable: true, filterable: true, visible: true },
    { key: 'primary_email', label: 'Email', sortable: true, filterable: true, visible: true,
      render: (row) => <span className="text-blue-600 text-xs">{row.primary_email}</span> },
    { key: 'company', label: 'Company', sortable: true, filterable: true, visible: true },
    { key: 'title', label: 'Title', sortable: true, filterable: true, visible: false },
    { key: 'city', label: 'City', sortable: true, filterable: true, visible: false },
    { key: 'state', label: 'State', sortable: true, filterable: true, visible: true },
    { key: 'target_name', label: 'Target', sortable: true, filterable: true, visible: true,
      render: (row) => row.target_name ? <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">{row.target_name}</Badge> : '—' },
    { key: 'batch_name', label: 'Batch', sortable: true, filterable: true, visible: true,
      render: (row) => <span className="text-[10px] text-gray-500 truncate max-w-[120px] block">{row.batch_name || '—'}</span> },
    { key: 'email_verify_result', label: 'Verified', sortable: true, filterable: true, visible: true,
      filterType: 'status',
      render: (row) => {
        if (!row.email_verified) return <Badge variant="outline" className="text-[10px] text-gray-400">Pending</Badge>;
        const c = { valid: 'bg-green-100 text-green-700', invalid: 'bg-red-100 text-red-700', risky: 'bg-yellow-100 text-yellow-700', unknown: 'bg-gray-100 text-gray-600' };
        return <Badge className={`${c[row.email_verify_result] || c.unknown} text-[10px]`}>{row.email_verify_result || 'unknown'}</Badge>;
      }},
    { key: 'status', label: 'Status', sortable: true, filterable: true, visible: true, filterType: 'status',
      render: (row) => {
        const c = { active: 'text-green-600 border-green-200', bounced: 'text-red-600 border-red-200', unsubscribed: 'text-gray-600 border-gray-200' };
        return <Badge variant="outline" className={`text-[10px] ${c[row.status] || 'text-gray-500 border-gray-200'}`}>{row.status}</Badge>;
      }},
    { key: 'total_sent', label: 'Sent', sortable: true, visible: true, sortType: 'number',
      render: (row) => <span className="text-xs text-gray-600">{row.total_sent || 0}</span> },
    { key: 'total_opens', label: 'Opens', sortable: true, visible: false, sortType: 'number' },
    { key: 'total_clicks', label: 'Clicks', sortable: true, visible: false, sortType: 'number' },
  ], []);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="max-w-[1600px] mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-indigo-100 rounded-lg p-2"><Mail className="w-6 h-6 text-indigo-600" /></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email List</h1>
            <p className="text-sm text-gray-500">Manage contacts for email campaigns</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={refreshAll}><RefreshCw className="w-4 h-4" /></Button>
          <Button onClick={() => setShowImport(true)} className="gap-2 bg-indigo-600 hover:bg-indigo-700">
            <Upload className="w-4 h-4" /> Import CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <StatCard icon={Users} label="Total Contacts" value={stats.total || 0} bgColor="bg-blue-50" color="text-blue-600" />
        <StatCard icon={CheckCircle2} label="Active" value={stats.active || 0} bgColor="bg-green-50" color="text-green-600" />
        <StatCard icon={Shield} label="Verified" value={stats.verified || 0} bgColor="bg-emerald-50" color="text-emerald-600" sub={stats.valid_emails ? `${stats.valid_emails} valid` : ''} />
        <StatCard icon={XCircle} label="Invalid" value={stats.invalid_emails || 0} bgColor="bg-red-50" color="text-red-600" />
        <StatCard icon={AlertTriangle} label="Risky" value={stats.risky_emails || 0} bgColor="bg-yellow-50" color="text-yellow-600" />
        <StatCard icon={Phone} label="With Phone" value={stats.with_phone || 0} bgColor="bg-purple-50" color="text-purple-600" />
        <StatCard icon={Eye} label="Total Opens" value={stats.total_opens || 0} bgColor="bg-amber-50" color="text-amber-600" />
      </div>

      {/* Filters: Typeahead + Dropdowns */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
        <div className="md:col-span-2">
          <TypeaheadInput value={search} onChange={setSearch} field="all" placeholder="Search name, email, company..." />
        </div>
        <TypeaheadInput value={batchNameFilter} onChange={v => { setBatchNameFilter(v); setPage(0); }} field="batch_name" placeholder="Filter by batch..." />
        <TypeaheadInput value={targetNameFilter} onChange={v => { setTargetNameFilter(v); setPage(0); }} field="target_name" placeholder="Filter by target..." />
        <div className="flex gap-2">
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
            className="flex-1 text-sm border border-gray-300 rounded-lg px-2 py-2 bg-white h-9">
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="unsubscribed">Unsubscribed</option>
            <option value="bounced">Bounced</option>
          </select>
          <select value={verifyFilter} onChange={e => { setVerifyFilter(e.target.value); setPage(0); }}
            className="flex-1 text-sm border border-gray-300 rounded-lg px-2 py-2 bg-white h-9">
            <option value="">All Verify</option>
            <option value="valid">Valid</option>
            <option value="invalid">Invalid</option>
            <option value="risky">Risky</option>
          </select>
        </div>
      </div>

      {/* Save as List button — shown when filters are active */}
      {hasFilters && (
        <div className="flex items-center justify-between mb-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
          <div className="flex items-center gap-2 text-sm text-blue-800">
            <List className="w-4 h-4" />
            <span><strong>{total.toLocaleString()}</strong> contacts match current filters</span>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setShowSaveList(true)} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
              <Save className="w-3.5 h-3.5" /> Save as List
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/admin/marketing/email-campaigns', {
                state: { prefillBatchName: batchNameFilter || undefined, prefillContactCount: total }
              })}
              className="gap-1.5"
            >
              <Send className="w-3.5 h-3.5" /> Create Campaign
            </Button>
          </div>
        </div>
      )}

      {/* Data Grid */}
      <Card>
        <CardContent className="p-0">
          <AdvancedDataGrid
            columns={gridColumns}
            data={contacts}
            rowIdKey="id"
            loading={loading}
            emptyMessage={stats.total > 0 ? 'No contacts match filters' : 'No contacts yet. Import a CSV to get started.'}
            showFilters={false}
            storageKey="jtcrm_email_list_cols"
          />

          {/* Server-side Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <span className="text-xs text-gray-500">
                {(page * LIMIT + 1).toLocaleString()}–{Math.min((page + 1) * LIMIT, total).toLocaleString()} of {total.toLocaleString()}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(0)}>First</Button>
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Button>
                <span className="text-sm text-gray-600 px-2">Page {page + 1} / {totalPages.toLocaleString()}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>Last</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {showImport && <ImportWizard onClose={() => setShowImport(false)} onComplete={refreshAll} tags={tags} />}

      {/* Save as List Modal */}
      {showSaveList && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSaveList(false)}>
          <Card className="w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Save Filtered Contacts as List</CardTitle>
                <button onClick={() => setShowSaveList(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <strong>{total.toLocaleString()}</strong> contacts match current filters
              </div>
              <div>
                <Label className="text-sm mb-1 block">List Name *</Label>
                <Input
                  value={saveListName}
                  onChange={e => setSaveListName(e.target.value)}
                  placeholder="e.g., Q2 HR Directors - Valid Emails"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-sm mb-1 block">Description</Label>
                <textarea
                  value={saveListDesc}
                  onChange={e => setSaveListDesc(e.target.value)}
                  placeholder="Optional description for this list..."
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="text-xs text-gray-400 space-y-1">
                <p className="font-medium text-gray-500">Active Filters:</p>
                {debouncedSearch && <p>Search: "{debouncedSearch}"</p>}
                {batchNameFilter && <p>Batch: {batchNameFilter}</p>}
                {targetNameFilter && <p>Target: {targetNameFilter}</p>}
                {statusFilter && <p>Status: {statusFilter}</p>}
                {verifyFilter && <p>Verification: {verifyFilter}</p>}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowSaveList(false)} className="flex-1">Cancel</Button>
                <Button onClick={saveAsList} disabled={savingList || !saveListName.trim()} className="flex-1 gap-1.5 bg-blue-600 hover:bg-blue-700">
                  {savingList ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Save List
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
